// Theme system — 'light' | 'dark' | 'auto', persisted in localStorage.
//
// 'auto' follows the OS via `prefers-color-scheme` and re-applies live when the
// system flips. We apply by toggling the `dark` class on <html>, which the
// Tailwind v4 custom variant (see tailwind.css) keys off. Mounted near the app
// root (entry.client.jsx) so the whole tree — every route — can read it.

import { createContext, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'nova-theme';
const THEMES = ['light', 'dark', 'auto'];

const ThemeContext = createContext(null);

function readStored() {
  if (typeof localStorage === 'undefined') return 'auto';
  const v = localStorage.getItem(STORAGE_KEY);
  return THEMES.includes(v) ? v : 'auto';
}

function prefersDark() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

// Resolve a theme choice to the actual mode to paint, then toggle the class.
function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  const dark = theme === 'dark' || (theme === 'auto' && prefersDark());
  document.documentElement.classList.toggle('dark', dark);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStored);

  // Paint whenever the choice changes.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // In 'auto', re-paint when the OS preference flips.
  useEffect(() => {
    if (theme !== 'auto' || typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('auto');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = (next) => {
    const value = THEMES.includes(next) ? next : 'auto';
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, value);
    setThemeState(value);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}

/**
 * The mode actually painted ('light' | 'dark'), for third-party widgets that
 * theme themselves (xyflow's `colorMode`) : it follows the app's `.dark` class
 * choice, not the OS — `'system'` would ignore an explicit light/dark pick.
 * Resolved from the theme choice (not by reading the class, which the provider
 * toggles in an effect that runs after children render) and re-resolved when
 * the OS flips in 'auto'. Outside a ThemeProvider (isolated tests) it reads the
 * `<html>` class once.
 */
export function useColorMode() {
  const ctx = useContext(ThemeContext);
  const theme = ctx?.theme;
  const [osDark, setOsDark] = useState(prefersDark);
  useEffect(() => {
    if (theme !== 'auto' || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setOsDark(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);
  if (!ctx) {
    return typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  }
  return theme === 'dark' || (theme === 'auto' && osDark) ? 'dark' : 'light';
}
